import React, { useMemo } from 'react';
import styled, { css, ThemeProvider } from 'styled-components';
import type { ProductListItemModel } from 'autogen/swagger/all';
import { hover } from 'variables';
import ProductFavoriteButton from 'components/generic/productFavoriteButton';
import ProductImage from 'components/generic/productImage';
import ProductName from 'components/generic/productName';
import type { IProductActionButton } from 'components/generic/productCTA/shared/types';
import useThemeHandler from 'components/shared/productItem/themes/useThemeHandler';
import ProductActionRenderer from 'components/generic/productCTA/ProductActionRenderer';
import useIsAuthenticated from 'hooks/user/useIsAuthenticated';
import { firstCharacterToLower } from 'modules/helpers/strings';
import {
    ProductActionContainer,
    ProductCard,
    ProductCardBottom,
    ProductCardMid,
    ProductCardTop,
    ProductContent
} from './shared/styles';
import ProductItemDebugger from './components/productDebug/ProductItemDebugger';
import serverSideTracking, { createProductItemTrackingContext } from './shared/helpers';
import { TrackingEvent } from './shared/types';
import { TagSplash } from '../Splash';
import LinkAction from '../Routing/components/LinkAction/LinkAction';
import useRequestContext from 'hooks/user/useRequestContext';

/* #region  styles */
const Anchor = styled(LinkAction)`
    min-width: 0;

    ${hover('hover')} {
        &:hover {
            text-decoration: none;
        }
    }
`;

const StyledProductName = styled(ProductName)`
    ${({ theme }) => theme.StyledProductName}
`;

const StyledProductFavoriteButton = styled(ProductFavoriteButton)`
    position: absolute;
    top: 0;
    right: 0;
    z-index: 1;
    margin: 12px;
`;
/* #endregion */

const ChanelProductItem = React.forwardRef<HTMLDivElement, any>(
    (
        {
            model,
            recommendationPlacement,
            ...restProps
        }: {
            model: ProductListItemModel;
            recommendationPlacement: string;
        },
        ref
    ) => {
        const {
            /* ----------------General Product Item properties-------------*/
            id: productId,
            externalProductId,
            linkAction,
            primaryImage,
            splashes,
            priceInfo: { minimumSalesQuantity },
            favoriteStatus: { favoriteStatus },
            standardName, // name of product
            listName, // Name, Brand Desc, AmountAndUnit
            /* ----------------For Product Action-------------*/
            action, // actionType for the CTA
            isSubscribable,
            isSubscriptionContext,
            addToBasketType,
            requireLoginForCheckout,
            fromPowerstep, // Whether the CTA was clicked from a powerstep only relevant for add to basket button
            bundleInfo, // Used when adding a bundle to basket, adds the params to the body of the req ( also used for abit of tracking )
            /* -----------------Tracking related-----------*/
            dataLayerDto,
            dataLayerDto: { listName: dataLayerListName },
            uaTrackingCategory,
            uaTrackingLabel,
            trackModel,
            showPowerstepOnAddToBasket,
            /* ----------------- Debug -----------*/
            explainedScore
        } = model;
        const isAuthenticated = useIsAuthenticated();

        const { posRequest } = useRequestContext();

        const trackingContext = createProductItemTrackingContext(
            firstCharacterToLower(uaTrackingCategory),
            uaTrackingLabel,
            trackModel,
            bundleInfo,
            recommendationPlacement
        );

        const productActionButtonModel: IProductActionButton['model'] = {
            action,
            trackingContext,
            productId,
            externalProductId,
            productNavigationLink: linkAction,
            isSubscriptionContext,
            isSubscribable,
            minimumSalesQuantity,
            bundleInfo,
            listName: dataLayerListName,
            fromPowerstep,
            addToBasketType,
            requireLoginForCheckout,
            recommendationPlacement,
            showPowerstepOnAddToBasket
        };

        const { ButtonStyling, ...theme } = useThemeHandler('chanel') as any;
        const stringifiedTrackModel = useMemo(() => JSON.stringify(trackModel), [trackModel]);
        const algonomyTracking = (trackingEvent: TrackingEvent) => {
            if (trackModel) {
                serverSideTracking(stringifiedTrackModel, trackingEvent);
            }
        };
        const showFavoriteButton = isAuthenticated || !posRequest?.isPosRequest;

        return (
            <ThemeProvider theme={theme}>
                <ProductCard
                    {...restProps}
                    size={{ sm: 'sm', lg: 'md' }}
                    ref={ref}
                    data-datalayer={JSON.stringify(dataLayerDto)}
                    onClick={() => algonomyTracking(TrackingEvent.ProductClick)}
                >
                    <ProductItemDebugger explainedScore={explainedScore} />

                    <ProductContent>
                        {showFavoriteButton && (
                            <StyledProductFavoriteButton
                                initialFavoriteState={favoriteStatus === 'IsFavorite'}
                                variant="chanel"
                                productId={productId}
                                onClick={() => algonomyTracking(TrackingEvent.AddToFavourites)}
                            />
                        )}

                        {/* Top Content */}
                        <ProductCardTop action={linkAction}>
                            {/* ProductImage - Displays the product image  */}
                            <ProductImage
                                image={{ ...primaryImage, alt: standardName }}
                                aria-label={standardName}
                                width={136}
                                transformType="SquareScale"
                            />
                        </ProductCardTop>

                        {/* Mid Content */}
                        <ProductCardMid>
                            {/* ProductName - Displays product name, description etc */}
                            <Anchor action={linkAction}>
                                <StyledProductName productName={listName} size="md" />
                                {splashes.secondary && <TagSplash {...splashes.secondary} />}
                            </Anchor>
                        </ProductCardMid>

                        {/* Bottom Content */}
                        <ProductCardBottom>
                            {/* ProductAction - Displays the current product action button */}
                            <ThemeProvider theme={{ ButtonStyling }}>
                                <ProductActionContainer>
                                    <ProductActionRenderer
                                        model={productActionButtonModel}
                                        css={css`
                                            flex: 1;
                                        `}
                                    />
                                </ProductActionContainer>
                            </ThemeProvider>
                        </ProductCardBottom>
                    </ProductContent>
                </ProductCard>
            </ThemeProvider>
        );
    }
);

export default ChanelProductItem;
